import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Flex } from 'antd'
import { useTranslation } from 'react-i18next'
import { currentLanguage } from '@/app/i18n'
import { grantAccess, type GuestAccessResult } from '@/features/portal/api'
import { isDefinitiveVoucherFailure } from '@/features/portal/pendingVoucher'
import {
  loginCard,
  loginCardRejectionKey,
  sameCard,
  type CardCredential,
  type EntryMethod,
  type VoucherCard,
} from '@/features/portal/voucherCard'
import { errorStatusAndTag, translatePortalError } from '@/shared/api/errors'
import { StepHeader } from '@/shared/ui/StepHeader'
import { CardEntry, type TypedEntry } from './card/CardEntry'

interface VoucherStepProps {
  zoneId: string
  mac: string
  ip: string | null
  nasIdentifier: string
  userAgent: string
  campaignId: string | null
  /** Kartadagi QR havolasi orqali eslab qolingan karta (`/v#...`) */
  pendingCard: VoucherCard | null
  autoSubmit: boolean
  onAccess: (result: GuestAccessResult) => void
  /** Eslab qolingan karta shu tarmoqda o'tmasligi aniq bo'ldi */
  onCardRejected: () => void
  onBack: () => void
}

interface Submission {
  card: CardCredential
  entryMethod: EntryMethod
}

/** `identifiedSale` hech qachon `true` yuborilmaydi — `credential.ts` dagi izoh. */
export function VoucherStep({
  zoneId,
  mac,
  ip,
  nasIdentifier,
  userAgent,
  campaignId,
  pendingCard,
  autoSubmit,
  onAccess,
  onCardRejected,
  onBack,
}: VoucherStepProps) {
  const { t } = useTranslation()
  const [code, setCode] = useState(pendingCard?.kind === 'code' ? pendingCard.code : '')
  const [typedEntry, setTypedEntry] = useState<TypedEntry>(pendingCard?.kind === 'code' ? 'CODE' : 'LOGIN')
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const autoSubmittedRef = useRef(false)

  const mutation = useMutation({
    mutationFn: ({ card, entryMethod }: Submission) =>
      grantAccess({
        zoneId,
        mac,
        ip,
        nasIdentifier,
        userAgent,
        credential: { method: 'VOUCHER', card },
        entryMethod,
        campaignId,
        locale: currentLanguage(),
      }),
  })

  const submit = (submission: Submission): void => {
    setError(null)
    mutation.mutate(submission, {
      onSuccess: onAccess,
      onError: (err) => {
        const { status, tag } = errorStatusAndTag(err)
        if (sameCard(submission.card, pendingCard) && isDefinitiveVoucherFailure(status, tag)) onCardRejected()
        const loginKey = submission.card.kind === 'login' ? loginCardRejectionKey(status, tag) : null
        setError(loginKey ? t(loginKey) : translatePortalError(err, t).message)
      },
    })
  }

  useEffect(() => {
    if (!autoSubmit || !pendingCard || autoSubmittedRef.current) return
    autoSubmittedRef.current = true
    submit({ card: pendingCard, entryMethod: 'QR_LINK' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const typedCode = code.replace(/\s+/g, '')

  const typedReady = typedEntry === 'LOGIN' ? login !== '' && password !== '' : typedCode !== ''

  const submitTyped = (): void => {
    if (typedEntry === 'LOGIN') {
      const card = loginCard(login, password)
      if (card) submit({ card, entryMethod: 'CODE' })
      else setError(t('voucher.loginInvalid'))
      return
    }
    if (!typedCode) {
      setError(t('voucher.codeInvalid'))
      return
    }
    submit({ card: { kind: 'code', code: typedCode }, entryMethod: 'CODE' })
  }

  return (
    <Flex vertical gap={16}>
      <StepHeader
        title={t('voucher.title')}
        subtitle={autoSubmit && pendingCard && mutation.isPending ? t('voucher.scannedSubtitle') : t('voucher.subtitle')}
      />

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <CardEntry
        code={code}
        onCodeChange={setCode}
        onCodeSubmit={submitTyped}
        onCard={(card, entryMethod) => submit({ card, entryMethod })}
        loginEntry={{
          mode: typedEntry,
          onModeChange: (mode) => {
            setTypedEntry(mode)
            setError(null)
          },
          login,
          password,
          onLoginChange: setLogin,
          onPasswordChange: setPassword,
        }}
        disabled={mutation.isPending}
      />

      <Flex vertical gap={8}>
        <Button
          size="large"
          block
          type={typedReady ? 'primary' : 'default'}
          loading={mutation.isPending}
          disabled={!typedReady}
          onClick={submitTyped}
        >
          {mutation.isPending ? t('voucher.submitting') : t('voucher.submit')}
        </Button>
        <Button type="text" block onClick={onBack} disabled={mutation.isPending}>
          {t('common.back')}
        </Button>
      </Flex>
    </Flex>
  )
}
